import React, { useState, useEffect, useRef } from 'react';

interface LogEntry {
  type: 'log' | 'warn' | 'error' | 'debug';
  message: string;
  timestamp: string;
}

export const DebugConsole: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only enable in dev or if specific localStorage flag is set
    const isDev = import.meta.env.DEV;
    const isDebugEnabled = localStorage.getItem('debug_mode') === 'true';

    if (!isDev && !isDebugEnabled) return;

    // Show button initially
    setIsVisible(true);

    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    const originalDebug = console.debug;

    const addLog = (type: LogEntry['type'], args: any[]) => {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');

      setLogs(prev => {
        const newLogs = [...prev, {
          type,
          message,
          timestamp: new Date().toISOString().split('T')[1].slice(0, 8)
        }];
        return newLogs.slice(-50); // Keep last 50 logs
      });
    };

    console.log = (...args) => {
      originalLog(...args);
      addLog('log', args);
    };

    console.warn = (...args) => {
      originalWarn(...args);
      addLog('warn', args);
    };

    console.error = (...args) => {
      originalError(...args);
      addLog('error', args);
    };
    
    console.debug = (...args) => {
        originalDebug(...args);
        // Uncomment to see debug logs in overlay
        // addLog('debug', args);
    };

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      console.debug = originalDebug;
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] pointer-events-none">
      <div className="bg-black/80 text-white font-mono text-xs p-2 max-h-[30vh] overflow-y-auto pointer-events-auto" ref={scrollRef}>
        <div className="flex justify-between items-center mb-2 sticky top-0 bg-black/90 p-1 border-b border-gray-700">
            <span>Debug Console</span>
            <button onClick={() => setLogs([])} className="bg-gray-700 px-2 py-1 rounded">Clear</button>
        </div>
        {logs.map((log, i) => (
          <div key={i} className={`mb-1 border-b border-white/10 pb-1 ${
            log.type === 'error' ? 'text-red-400' : 
            log.type === 'warn' ? 'text-yellow-400' : 'text-gray-300'
          }`}>
            <span className="text-gray-500 mr-2">[{log.timestamp}]</span>
            <span className="break-all whitespace-pre-wrap">{log.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


